import React, { useState, useEffect } from 'react';
import {
  Search,
  Truck,
  Droplets,
  Sofa,
  Package,
  CheckCircle,
  UserCheck,
  Home,
  Calendar,
  User,
  Phone,
  WashingMachine,
  Filter,
  X
} from 'lucide-react';
import { API_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import OrderLogScreen from './OrderLogScreen';
import FloatingField from '../components/FloatingField';

const OrderLog = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    service_type: 'all',
    priority: 'all',
    payment_status: 'all',
    client_type: 'all',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Этапы и их настройки
  const stagesConfig = {
    new: { label: 'Новый', icon: Package, color: 'gray' },
    pickup: { label: 'Доставка от клиента', icon: Truck, color: 'blue' },
    washing: { label: 'Стирка', icon: Droplets, color: 'cyan' },
    drying_packing: { label: 'Сушка и упаковка', icon: Package, color: 'green' },
    approval: { label: 'Согласование', icon: CheckCircle, color: 'yellow' },
    delivery: { label: 'Доставка клиенту', icon: Home, color: 'purple' },
    handover: { label: 'Выдача', icon: UserCheck, color: 'emerald' },
    cleaning: { label: 'Химчистка мебели', icon: Sofa, color: 'orange' },
    completed: { label: 'Завершён', icon: CheckCircle, color: 'green' },
    cancelled: { label: 'Отменён', icon: X, color: 'red' }
  };

  // Загрузка заказов
  const loadOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.service_type !== 'all') params.append('service_type', filters.service_type);
      if (filters.priority !== 'all') params.append('priority', filters.priority);
      if (filters.client_type !== 'all') params.append('client_type', filters.client_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '500');

      const response = await fetch(`${API_URL}/api/orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery]);

  // Фильтрация на клиенте
  const filteredOrders = orders.filter(order => {
    if (filters.payment_status !== 'all' && order.payment_status !== filters.payment_status) return false;
    return true;
  });

  // Статистика
  const stats = {
    total: orders.length,
    new: orders.filter(o => o.status === 'new').length,
    in_progress: orders.filter(o => !['completed', 'cancelled', 'new'].includes(o.status)).length,
    completed: orders.filter(o => o.status === 'completed').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
    total_amount: orders.reduce((sum, o) => sum + (o.final_amount || 0), 0)
  };

  const getServiceTypeLabel = (type) => {
    const labels = { washing: 'Стирка', cleaning: 'Химчистка мебели', extra_order: 'Доп. услуги' };
    return labels[type] || type;
  };

  const getServiceTypeIcon = (type) => {
    if (type === 'washing') return WashingMachine;
    if (type === 'cleaning') return Sofa;
    return Package;
  };

  const getStatusLabel = (status) => {
    return stagesConfig[status]?.label || status;
  };

  const getStatusIcon = (status) => {
    return stagesConfig[status]?.icon || Package;
  };

  const getStatusColor = (status) => {
    return stagesConfig[status]?.color || 'gray';
  };

  const getPriorityLabel = (priority) => {
    const labels = { normal: 'Обычный', urgent: 'Срочный', vip: 'VIP' };
    return labels[priority] || priority;
  };

  const getPriorityColor = (priority) => {
    if (priority === 'urgent') return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200';
    if (priority === 'vip') return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';
  };

  return (
    <div className="space-y-6 desktop-content-container-wide">
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ml-auto"
          >
            <Filter className="w-4 h-4" />
            <span>Фильтры</span>
          </button>
        </div>

        {/* Статистика */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Всего</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.new}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Новые</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-xl">
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-200">{stats.in_progress}</div>
            <div className="text-sm text-blue-600 dark:text-blue-300">В работе</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900 p-4 rounded-xl">
            <div className="text-2xl font-bold text-green-700 dark:text-green-200">{stats.completed}</div>
            <div className="text-sm text-green-600 dark:text-green-300">Завершены</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900 p-4 rounded-xl">
            <div className="text-2xl font-bold text-red-700 dark:text-red-200">{stats.cancelled}</div>
            <div className="text-sm text-red-600 dark:text-red-300">Отменены</div>
          </div>
        </div>

        {/* Поиск */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Поиск по клиенту, телефону или номеру заказа..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>

        {/* Фильтры */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div>
              <FloatingField label="Этап">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="all">Все этапы</option>
                  {Object.entries(stagesConfig).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </FloatingField>
            </div>
            <div>
              <FloatingField label="Тип услуги">
                <select
                value={filters.service_type}
                onChange={(e) => setFilters({...filters, service_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="all">Все услуги</option>
                <option value="washing">Стирка</option>
                <option value="cleaning">Химчистка</option>
                <option value="extra_order">Доп. услуги</option>
                </select>
              </FloatingField>
            </div>
            <div>
              <FloatingField label="Приоритет">
                <select
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="all">Все</option>
                <option value="normal">Обычный</option>
                <option value="urgent">Срочный</option>
                <option value="vip">VIP</option>
                </select>
              </FloatingField>
            </div>
            <div>
              <FloatingField label="Тип клиента">
                <select
                  value={filters.client_type}
                  onChange={(e) => setFilters({...filters, client_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="all">Все клиенты</option>
                  <option value="individual">Физическое лицо</option>
                  <option value="company">Организация</option>
                </select>
              </FloatingField>
            </div>
            <div>
              <FloatingField label="Дата от">
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 dark:text-gray-100"
                />
              </FloatingField>
            </div>
            <div>
              <FloatingField label="Дата до">
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 dark:text-gray-100"
                />
              </FloatingField>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ status: 'all', service_type: 'all', priority: 'all', payment_status: 'all', client_type: 'all', date_from: '', date_to: '' })}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Сбросить
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Список заказов */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Загрузка заказов...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Заказы не найдены</h3>
            <p className="text-gray-600 dark:text-gray-400">Попробуйте изменить фильтры</p>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const StatusIcon = getStatusIcon(order.status);
            const statusColor = getStatusColor(order.status);
            const ServiceIcon = getServiceTypeIcon(order.service_type);

            return (
              <div key={order.id} className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg bg-${statusColor}-100 dark:bg-${statusColor}-900`}>
                      <StatusIcon className={`w-5 h-5 text-${statusColor}-600 dark:text-${statusColor}-400`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{order.order_number}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{getStatusLabel(order.status)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{order.final_amount} ₽</div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                      {getPriorityLabel(order.priority)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{order.client_name || 'Не указан'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{order.client_phone || 'Не указан'}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(order.created_date).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ServiceIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{getServiceTypeLabel(order.service_type)}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {order.pickup_date && (
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Забор: {new Date(order.pickup_date).toLocaleDateString('ru-RU')} {order.pickup_time_slot || ''}
                        </span>
                      </div>
                    )}
                    {order.items?.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Package className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Позиций: {order.items.length}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>Оплата: {order.payment_status === 'paid' ? 'Оплачен' : order.payment_status === 'pending' ? 'Не оплачен' : order.payment_status}</span>
                    {order.payment_method && <span>• {order.payment_method === 'cash' ? 'Наличные' : order.payment_method === 'card' ? 'Карта' : order.payment_method}</span>}
                  </div>
                  <button
                    onClick={() => navigate(`/order/${order.id}?from=orders-log`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Подробнее
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

void OrderLog;

export default OrderLogScreen;
